import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, isMasterAdmin } from '../../utils/auth';
import { getPatients, getUsers, getEventUsers, getFeeCollections, getExpenses, getCashHoldings, getCashTransfers, addCashTransfer, addLog, getEventById } from '../../utils/storage';
import { getActiveEventId } from '../../utils/event';
import DashboardLayout from '../../components/feature/DashboardLayout';
import type { CashTransfer, User } from '../../types';
import { getDeviceInfo, getIPAddress } from '../../utils/device';

export default function Dashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [totalPatients, setTotalPatients] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netProfit, setNetProfit] = useState(0);
  const [cashHoldings, setCashHoldings] = useState<Array<{ userId: string; username: string; amount: number }>>([]);
  const [eventUsersList, setEventUsersList] = useState<User[]>([]);
  const [cashTransfers, setCashTransfers] = useState<CashTransfer[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFrom, setTransferFrom] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferRemarks, setTransferRemarks] = useState('');
  const [revenueChartData, setRevenueChartData] = useState<Array<{ date: string; amount: number }>>([]);
  const [eventName, setEventName] = useState('');
  const [eventStatus, setEventStatus] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // Re-fetch all event-scoped data when the active event is switched
  useEffect(() => {
    const handler = () => setReloadKey((k) => k + 1);
    window.addEventListener('active-event-changed', handler);
    return () => window.removeEventListener('active-event-changed', handler);
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Load active event details for the banner
    getEventById(getActiveEventId()).then((ev) => {
      if (ev) {
        setEventName(ev.eventName);
        setEventStatus(ev.status);
      } else {
        setEventName('Medsparkz');
      }
    }).catch(() => setEventName('Medsparkz'));

    const loadData = async () => {
      const patients = await getPatients();
      setTotalPatients(patients.length);

      // Event-scoped user counts
      const eventUsers = await getEventUsers(getActiveEventId());
      setEventUsersList(eventUsers);
      const active = eventUsers.filter(u => u.isActive !== false).length;
      const online = eventUsers.filter(u => u.isOnline === true).length;
      setActiveUsers(active);
      setOnlineUsers(online);

      if (user.role === 'master_admin' || user.role === 'admin') {
        const collections = await getFeeCollections();
        const revenue = collections.reduce((sum, c) => sum + c.amount, 0);
        setTotalRevenue(revenue);

        const expenses = await getExpenses();
        const expenseTotal = expenses.reduce((sum, e) => sum + e.totalAmount, 0);
        setTotalExpenses(expenseTotal);

        setNetProfit(revenue - expenseTotal);

        const holdings = await getCashHoldings();
        setCashHoldings(holdings);

        const transfers = await getCashTransfers();
        setCashTransfers(transfers);

        // Revenue chart data - group by date
        const revenueByDate: Record<string, number> = {};
        collections.forEach(c => {
          const date = new Date(c.collectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          revenueByDate[date] = (revenueByDate[date] || 0) + c.amount;
        });
        const chartData = Object.entries(revenueByDate).map(([date, amount]) => ({ date, amount })).slice(-7);
        setRevenueChartData(chartData);
      }
    };

    loadData();
  }, [user, navigate, reloadKey]);

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !transferFrom || !transferTo || !transferAmount) return;

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    const holdings = await getCashHoldings();
    const fromHolding = holdings.find(h => h.userId === transferFrom);
    if (!fromHolding || fromHolding.amount < amount) {
      alert('Insufficient cash balance');
      return;
    }

    const users = eventUsersList.length > 0 ? eventUsersList : await getUsers();
    const fromUser = users.find(u => u.id === transferFrom);
    const toUser = users.find(u => u.id === transferTo);

    if (!fromUser || !toUser) {
      alert('Invalid user selected. Please select valid From and To users.');
      return;
    }

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();

    const transfer: CashTransfer = {
      id: Date.now().toString(),
      fromUserId: transferFrom,
      fromUsername: fromUser.username,
      toUserId: transferTo,
      toUsername: toUser.username,
      amount,
      remarks: transferRemarks,
      transferredAt: new Date().toISOString(),
      transferredBy: user.username,
    };

    await addCashTransfer(transfer);

    await addLog({
      id: (Date.now() + 1).toString(),
      userId: user.id,
      username: user.username,
      action: 'Cash Transfer',
      details: `Transferred Rs.${amount} from ${fromUser.username} to ${toUser.username}. Remarks: ${transferRemarks || 'N/A'}`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    setCashTransfers([transfer, ...cashTransfers]);
    const updatedHoldings = await getCashHoldings();
    setCashHoldings(updatedHoldings);

    setShowTransferModal(false);
    setTransferFrom('');
    setTransferTo('');
    setTransferAmount('');
    setTransferRemarks('');
  };

  if (!user) return null;

  const isMasterAdminUser = user.role === 'master_admin';
  const isAdminOrMaster = user.role === 'admin' || user.role === 'master_admin';

  const maxRevenueAmount = Math.max(...revenueChartData.map(d => d.amount), 1);

  // Get display name or fallback to username
  const displayName = user.displayName || user.username;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-1">Welcome back, <span className="font-semibold text-teal-600">{displayName}</span></p>
        </div>

        {/* Active event banner */}
        <div className="mb-8 bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-calendar-event-line text-xl text-teal-600"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Current Event</p>
              <p className="text-lg font-bold text-gray-800">{eventName || 'Medsparkz'}</p>
            </div>
            {eventStatus && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize ml-1 ${
                eventStatus === 'active' ? 'bg-green-100 text-green-700'
                : eventStatus === 'archived' ? 'bg-gray-100 text-gray-600'
                : eventStatus === 'completed' ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-700'
              }`}>
                {eventStatus}
              </span>
            )}
          </div>
          {isMasterAdmin(user) && (
            <button
              onClick={() => navigate('/events')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-swap-line"></i>Switch / Manage Events
            </button>
          )}
        </div>

        {/* Master Admin Stats */}
        {isMasterAdminUser && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Overview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                    <i className="ri-user-add-line text-lg text-teal-600"></i>
                  </div>
                  <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Patients</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{totalPatients}</p>
                <p className="text-xs text-gray-500 mt-1">Total Registrations</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <i className="ri-user-follow-line text-lg text-emerald-600"></i>
                  </div>
                  <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Users</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{activeUsers}</p>
                <p className="text-xs text-gray-500 mt-1">Active Users</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <i className="ri-radio-button-line text-lg text-green-500"></i>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Live</span>
                </div>
                <p className="text-3xl font-bold text-gray-800">{onlineUsers}</p>
                <p className="text-xs text-gray-500 mt-1">Currently Online</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-user-line text-lg text-amber-600"></i>
                  </div>
                  <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Role</span>
                </div>
                <p className="text-sm font-bold text-gray-800 mt-1">Master Admin</p>
                <p className="text-xs text-gray-500 mt-1">Full system access</p>
              </div>
            </div>
          </div>
        )}

        {/* Financial Overview for Admin & Master Admin */}
        {isAdminOrMaster && (
          <>
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Financial Overview</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <i className="ri-money-rupee-circle-line text-lg text-green-600"></i>
                    </div>
                    <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Revenue</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-800">₹{totalRevenue.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Collections</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-shopping-cart-line text-lg text-red-600"></i>
                    </div>
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Expenses</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-800">₹{totalExpenses.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">Total Spending</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                      <i className="ri-line-chart-line text-lg text-teal-600"></i>
                    </div>
                    <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Net Profit</span>
                  </div>
                  <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{netProfit.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Revenue - Expenses</p>
                </div>
              </div>
            </div>

            {/* Revenue Chart */}
            <div className="mb-8">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-semibold text-gray-800">Revenue Trend</h3>
                  <span className="text-xs text-gray-500">Last 7 days</span>
                </div>
                {revenueChartData.length > 0 ? (
                  <div className="flex items-end justify-between gap-3 h-48">
                    {revenueChartData.map((item, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100%' }}>
                          <div
                            className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg absolute bottom-0 transition-all duration-500 hover:from-teal-600 hover:to-teal-500 cursor-pointer"
                            style={{ height: `${(item.amount / maxRevenueAmount) * 100}%` }}
                            title={`₹${item.amount}`}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 font-medium">{item.date}</p>
                        <p className="text-xs text-teal-600 font-semibold">₹{item.amount}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                    No revenue data available
                  </div>
                )}
              </div>
            </div>

            {/* Cash Holdings */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Cash Holdings</h3>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-exchange-line"></i>
                  Transfer Cash
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {cashHoldings.length > 0 ? (
                  cashHoldings.map((holding) => (
                    <div key={holding.userId} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                          <i className="ri-wallet-3-line text-lg text-amber-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{holding.username}</p>
                          <p className="text-xs text-gray-500">Cash in Hand</p>
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-800">₹{holding.amount.toFixed(2)}</p>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <i className="ri-wallet-3-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">No cash holdings recorded yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Cash Transfer History */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Cash Transfer History</h3>
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {cashTransfers.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Transfer ID</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">From</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">To</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remarks</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Transferred By</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date & Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {cashTransfers.map((transfer) => (
                          <tr key={transfer.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">{transfer.id}</td>
                            <td className="px-4 py-3 text-sm text-gray-800 font-medium">{transfer.fromUsername}</td>
                            <td className="px-4 py-3 text-sm text-gray-800 font-medium">{transfer.toUsername}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-teal-600">₹{transfer.amount.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{transfer.remarks || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{transfer.transferredBy}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {new Date(transfer.transferredAt).toLocaleString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <i className="ri-exchange-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500">No cash transfers recorded yet</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* General Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="w-11 h-11 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-hospital-line text-xl text-teal-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">Medical Camp Portal</h3>
            <p className="text-xs text-gray-500 mt-1">Use the sidebar to navigate between modules.</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="w-11 h-11 bg-amber-50 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-user-line text-xl text-amber-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">Your Role</h3>
            <p className="text-xs text-gray-500 mt-1 capitalize">
              {user.role.split('_').join(' ')}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <div className="w-11 h-11 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-time-line text-xl text-emerald-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-800">Session</h3>
            <p className="text-xs text-gray-500 mt-1">
              Logged in at {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      {/* Cash Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800">Transfer Cash</h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            <form onSubmit={handleTransferSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From User</label>
                <select
                  value={transferFrom}
                  onChange={(e) => setTransferFrom(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                >
                  <option value="">Select user</option>
                  {cashHoldings.filter(h => h.amount > 0).map((holding) => (
                    <option key={holding.userId} value={holding.userId}>
                      {holding.username} (₹{holding.amount.toFixed(2)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To User</label>
                <select
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                >
                  <option value="">Select user</option>
                  {eventUsersList
                    .filter(u => u.id !== transferFrom)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName || u.username}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter amount"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks (Optional)</label>
                <textarea
                  value={transferRemarks}
                  onChange={(e) => setTransferRemarks(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  rows={3}
                  placeholder="Add any notes about this transfer"
                ></textarea>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}